import { Injectable } from '@angular/core';

/**
 * Magasin des fichiers en attente d'envoi — aujourd'hui les photos de
 * justificatif prises sans réseau.
 *
 * Pourquoi pas Preferences, où vit déjà la file elle-même : une photo
 * compressée pèse plusieurs centaines de kilo-octets une fois en base64, et
 * Preferences s'adosse à SharedPreferences / UserDefaults — un magasin de
 * petites valeurs que la plateforme charge d'un bloc en mémoire. Y ranger des
 * images dégraderait tout le reste de l'application.
 *
 * IndexedDB est le bon support ici, et il est de toute façon déjà requis : la
 * persistance de Firestore et celle de Firebase Auth en dépendent. S'il venait
 * à manquer, mieux vaut le dire à l'utilisateur que faire disparaître son
 * justificatif en silence — les erreurs remontent donc à l'appelant.
 */

const DB_NAME = 'te-outbox-blobs';
const STORE = 'blobs';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const idb = globalThis.indexedDB;
    if (!idb) {
      reject(new Error('Stockage local indisponible.'));
      return;
    }
    const request = idb.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) {
        request.result.createObjectStore(STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Stockage local indisponible.'));
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const request = run(db.transaction(STORE, mode).objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('Écriture locale impossible.'));
    });
  } finally {
    db.close();
  }
}

@Injectable({ providedIn: 'root' })
export class OutboxBlobs {
  /** Conserve un dataURL sous une clé. Rejette si le stockage est indisponible. */
  async put(key: string, dataUrl: string): Promise<void> {
    await withStore('readwrite', (store) => store.put(dataUrl, key));
  }

  /** Rend le dataURL conservé, ou `null` s'il a disparu. */
  async get(key: string): Promise<string | null> {
    try {
      return (await withStore<string | undefined>('readonly', (s) => s.get(key))) ?? null;
    } catch {
      return null;
    }
  }

  /** Supprime un fichier envoyé ou abandonné. Sans effet s'il n'existe plus. */
  async remove(key: string): Promise<void> {
    try {
      await withStore('readwrite', (store) => store.delete(key));
    } catch {
      // Le fichier restera au pire orphelin : ne jamais faire échouer un envoi
      // réussi sur un ménage raté.
    }
  }
}
